import axios from "axios";
import { apiURL } from "./constants";

// Get all site settings
export const getSiteSettings = async () => {
  console.log('API: getSiteSettings called');
  try {
    const response = await axios.get(`${apiURL}/api/settings/site-settings/`, {
      headers: { "Content-Type": "application/json" },
      withCredentials: true,
    });
    
    // Process the response to ensure image URLs are absolute
    const data = response.data;
    if (data.site_icon && !data.site_icon.startsWith('http')) {
      data.site_icon = `${apiURL}${data.site_icon}`;
    }
    if (data.welcome_background && !data.welcome_background.startsWith('http')) {
      data.welcome_background = `${apiURL}${data.welcome_background}`;
    }
    
    return data;
  } catch (error) {
    console.error("Error fetching site settings:", error);
    // Return fallback settings if API fails
    return {
      site_name: "",
      site_name_ar: "",
      site_name_zh: "",
      site_icon: "",
      welcome_background: "",
      cr_number: "",
      address: "",
      vat_number: "",
      bank_details: "",
    };
  }
};

// Update site settings and clear cache
export const updateSiteSettings = async (settingsData) => {
  try {
    const accessToken = localStorage.getItem('access_token');
    const response = await axios.post(`${apiURL}/api/settings/update-site-settings/`, settingsData, {
      headers: { 
        "Content-Type": "application/json",
        'Authorization': `Bearer ${accessToken}`
      },
      withCredentials: true,
    });
    
    // Clear the cache after successful update
    if (typeof window !== 'undefined') {
      try {
        localStorage.removeItem('site_settings_cache');
        localStorage.removeItem('site_settings_cache_timestamp');
        console.log('Site settings cache cleared after update');
      } catch (error) {
        console.error('Error clearing cache:', error);
      }
    }
    
    return response.data;
  } catch (error) {
    console.error("Error updating site settings:", error);
    throw error;
  }
};

// Get all setting files
export const getSettingFiles = async () => {
  console.log('API: getSettingFiles called');
  try {
    const accessToken = localStorage.getItem('access_token');
    const response = await axios.get(`${apiURL}/api/settings/files/`, {
      headers: { 
        "Content-Type": "application/json",
        'Authorization': `Bearer ${accessToken}`
      },
      withCredentials: true,
    });
    return response.data;
  } catch (error) {
    console.error("Error fetching setting files:", error);
    return [];
  }
};

// Get specific setting file
export const getSettingFile = async (name) => {
  try {
    const accessToken = localStorage.getItem('access_token');
    const response = await axios.get(`${apiURL}/api/settings/files/${name}/`, {
      headers: { 
        "Content-Type": "application/json",
        'Authorization': `Bearer ${accessToken}`
      },
      withCredentials: true,
    });
    return response.data;
  } catch (error) {
    console.error(`Error fetching setting file ${name}:`, error);
    return null;
  }
};

// Upload setting file
export const uploadSettingFile = async (formData) => {
  try {
    const accessToken = localStorage.getItem('access_token');
    const response = await axios.post(`${apiURL}/api/settings/upload-file/`, formData, {
      headers: { 
        "Content-Type": "multipart/form-data",
        'Authorization': `Bearer ${accessToken}`
      },
      withCredentials: true,
    });
    return response.data;
  } catch (error) {
    console.error("Error uploading setting file:", error);
    throw error;
  }
};

// Update setting file
export const updateSettingFile = async (name, formData) => {
  try {
    const accessToken = localStorage.getItem('access_token');
    const response = await axios.put(`${apiURL}/api/settings/update-file/${name}/`, formData, {
      headers: { 
        "Content-Type": "multipart/form-data",
        'Authorization': `Bearer ${accessToken}`
      },
      withCredentials: true,
    });
    return response.data;
  } catch (error) {
    console.error(`Error updating setting file ${name}:`, error);
    throw error;
  }
};

// Delete setting file
export const deleteSettingFile = async (name) => {
  try {
    const accessToken = localStorage.getItem('access_token');
    const response = await axios.delete(`${apiURL}/api/settings/delete-file/${name}/`, {
      headers: { 
        'Authorization': `Bearer ${accessToken}`
      },
      withCredentials: true,
    });
    return response.data;
  } catch (error) {
    console.error(`Error deleting setting file ${name}:`, error);
    throw error;
  }
};

// Get all settings (admin only)
export const getAllSettings = async () => {
  try {
    const accessToken = localStorage.getItem('access_token');
    const response = await axios.get(`${apiURL}/api/settings/settings/`, {
      headers: { 
        "Content-Type": "application/json",
        'Authorization': `Bearer ${accessToken}`
      },
      withCredentials: true,
    });
    return response.data;
  } catch (error) {
    console.error("Error fetching all settings:", error);
    throw error;
  }
};

// Get specific setting by ID
export const getSettingById = async (settingId) => {
  try {
    const accessToken = localStorage.getItem('access_token');
    const response = await axios.get(`${apiURL}/api/settings/setting/${settingId}/`, {
      headers: { 
        "Content-Type": "application/json",
        'Authorization': `Bearer ${accessToken}`
      },
      withCredentials: true,
    });
    return response.data;
  } catch (error) {
    console.error(`Error fetching setting ${settingId}:`, error);
    throw error;
  }
};

// Update specific setting by ID
export const updateSettingById = async (settingId, settingData) => {
  try {
    const accessToken = localStorage.getItem('access_token');
    const response = await axios.put(`${apiURL}/api/settings/setting/${settingId}/`, settingData, {
      headers: { 
        "Content-Type": "application/json",
        'Authorization': `Bearer ${accessToken}`
      },
      withCredentials: true,
    });
    return response.data;
  } catch (error) {
    console.error(`Error updating setting ${settingId}:`, error);
    throw error;
  }
};

/**
 * TODO(permissions #144): No UI consumer today.
 * Permission: PERMISSIONS.SETTINGS_DELETE_DETAIL.
 * When wiring up, gate the caller via <PermissionGate /> or hasPermission().
 * See ENDPOINTS_USAGE.md Phase 7.
 */
// Delete specific setting by ID
export const deleteSettingById = async (settingId) => {
  try {
    const accessToken = localStorage.getItem('access_token');
    const response = await axios.delete(`${apiURL}/api/settings/setting/${settingId}/`, {
      headers: { 
        'Authorization': `Bearer ${accessToken}`
      },
      withCredentials: true,
    });
    return response.data;
  } catch (error) {
    console.error(`Error deleting setting ${settingId}:`, error);
    throw error;
  }
};

// Upload site icon
export const uploadSiteIcon = async (file) => {
  try {
    const accessToken = localStorage.getItem('access_token');
    const formData = new FormData();
    formData.append('file', file);
    
    const response = await axios.post(`${apiURL}/api/settings/upload-site-icon/`, formData, {
      headers: { 
        'Content-Type': 'multipart/form-data',
        'Authorization': `Bearer ${accessToken}`
      },
      withCredentials: true,
      timeout: 30000, // 30 seconds for file uploads
    });
    
    // Clear the cache after successful upload
    if (typeof window !== 'undefined') {
      try {
        localStorage.removeItem('site_settings_cache');
        localStorage.removeItem('site_settings_cache_timestamp');
        console.log('Site settings cache cleared after icon upload');
      } catch (error) {
        console.error('Error clearing cache:', error);
      }
    }
    
    return response.data;
  } catch (error) {
    console.error("Error uploading site icon:", error);
    if (error.response) {
      // Server responded with error
      throw new Error(error.response.data.error || 'Failed to upload site icon');
    } else if (error.request) {
      // Request made but no response
      throw new Error('Network error. Please check your connection.');
    } else {
      // Something else happened
      throw new Error(error.message || 'Failed to upload site icon');
    }
  }
};

// Upload welcome background
export const uploadWelcomeBackground = async (file) => {
  try {
    const accessToken = localStorage.getItem('access_token');
    const formData = new FormData();
    formData.append('file', file);
    
    const response = await axios.post(`${apiURL}/api/settings/upload-welcome-background/`, formData, {
      headers: { 
        'Content-Type': 'multipart/form-data',
        'Authorization': `Bearer ${accessToken}`
      },
      withCredentials: true,
      timeout: 30000, // 30 seconds for file uploads
    });
    
    // Clear the cache after successful upload
    if (typeof window !== 'undefined') {
      try {
        localStorage.removeItem('site_settings_cache');
        localStorage.removeItem('site_settings_cache_timestamp');
        console.log('Site settings cache cleared after welcome background upload');
      } catch (error) {
        console.error('Error clearing cache:', error);
      }
    }
    
    return response.data;
  } catch (error) {
    console.error("Error uploading welcome background:", error);
    if (error.response) {
      // Server responded with error
      throw new Error(error.response.data.error || 'Failed to upload welcome background');
    } else if (error.request) {
      // Request made but no response
      throw new Error('Network error. Please check your connection.');
    } else {
      // Something else happened
      throw new Error(error.message || 'Failed to upload welcome background');
    }
  }
};
